<template>
  <div class="campanhas-oficiais">
    <!-- Loading Fullscreen Centralizado (apenas para criação de campanha) -->
    <div
      v-if="carregando"
      class="loading-overlay"
    >
      <div class="loading-content">
        <b-spinner
          variant="primary"
          style="width: 4rem; height: 4rem;"
        />
        <h4 class="mt-3 text-primary font-weight-bold">
          {{ mensagemLoading }}
        </h4>
        <p class="text-muted mb-0">
          Por favor, aguarde...
        </p>
      </div>
    </div>

    <!-- Header -->
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h3 class="mb-0">
            Todas as campanhas
          </h3>
          <p class="text-muted mb-0">
            Gerencie todas as campanhas da loja
          </p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <!-- Alerta de Status do Telefone WhatsApp -->
          <b-alert
            v-if="statusTelefone"
            :variant="variantStatus"
            show
            class="mb-0 d-flex align-items-center"
            style="padding: 0.5rem 1rem;"
          >
            <feather-icon
              :icon="iconStatus"
              size="18"
              class="mr-50"
            />
            <div class="d-flex align-items-center">
              <strong class="mr-50">{{ tituloStatus }}</strong>
              <span
                v-if="statusTelefone.data"
                class="text-muted small"
              >
                {{ statusTelefone.data.display_phone_number }}
              </span>
            </div>
          </b-alert>

          <b-button
            @click="abrirModalEnvioTeste"
          >
            <feather-icon
              icon="SendIcon"
              class="mr-50"
            />
            Envio Teste
          </b-button>
          <b-button
            variant="primary"
            @click="abrirModalNovaCampanha"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            Nova Campanha
          </b-button>
        </div>
      </div>

      <!-- Filtros -->
      <div class="d-flex gap-2 mb-2">
        <b-form-input
          v-model="busca"
          placeholder="Buscar campanha..."
          class="mr-2"
          style="max-width: 300px;"
        />
        <b-form-select
          v-model="filtroStatus"
          :options="statusOptions"
          style="max-width: 200px;"
        />
      </div>
    </b-card>

    <!-- Estatísticas/Métricas -->
    <div
      v-if="estatisticas"
      class="mb-2"
    >
      <!-- Primeira linha: Campanhas e Conversões -->
      <div class="stats-grid-primary mt-2 mb-2">
        <b-card class="stat-card stat-card-highlight">
          <div class="stat-icon stat-icon-large">
            <feather-icon
              icon="LayoutIcon"
              size="32"
              class="text-primary"
            />
          </div>
          <div class="stat-content">
            <h3 class="stat-value-large mb-0">
              {{ estatisticas.total_campanhas || 0 }}
            </h3>
            <p class="stat-label text-muted mb-1">
              Total de Campanhas
            </p>
            <div class="stat-breakdown">
              <span class="badge badge-light-success mr-1">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="12"
                />
                {{ estatisticas.campanhas_ativas || 0 }} Ativas
              </span>
              <span class="badge badge-light-secondary mr-1">
                <feather-icon
                  icon="PauseCircleIcon"
                  size="12"
                />
                {{ estatisticas.campanhas_inativas || 0 }} Inativas
              </span>
              <span
                v-b-tooltip.hover
                class="badge badge-light-danger cursor-pointer"
                title="Clique para ver campanhas deletadas"
                @click="abrirModalCampanhasDeletadas"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="12"
                />
                {{ estatisticas.campanhas_deletadas || 0 }} Deletadas
              </span>
            </div>
          </div>
        </b-card>

        <b-card class="stat-card stat-card-highlight">
          <div class="stat-icon stat-icon-large">
            <feather-icon
              icon="ShoppingCartIcon"
              size="32"
              class="text-success"
            />
          </div>
          <div class="stat-content">
            <h3 class="stat-value-large mb-0">
              {{ estatisticas.total_conversoes || 0 }}
            </h3>
            <p class="stat-label text-muted mb-1">
              Total de Conversões
            </p>
            <div class="stat-breakdown">
              <span class="badge badge-light-success mr-1">
                <feather-icon
                  icon="ZapIcon"
                  size="12"
                />
                {{ estatisticas.conversoes_diretas || 0 }} Diretas
              </span>
              <span class="badge badge-light-info">
                <feather-icon
                  icon="ClockIcon"
                  size="12"
                />
                {{ estatisticas.conversoes_janela_tempo || 0 }} Janela
              </span>
            </div>
          </div>
        </b-card>

        <b-card class="stat-card stat-card-highlight">
          <div class="stat-icon stat-icon-large">
            <feather-icon
              icon="DollarSignIcon"
              size="32"
              class="text-warning"
            />
          </div>
          <div class="stat-content">
            <h3 class="stat-value-large mb-0">
              {{ formatarMoeda(estatisticas.valor_total_conversoes) }}
            </h3>
            <p class="stat-label text-muted mb-1">
              Lucro Total
            </p>
            <div class="stat-breakdown">
              <span class="badge badge-light-success mr-1">
                <feather-icon
                  icon="ZapIcon"
                  size="12"
                />
                {{ formatarMoeda(estatisticas.valor_conversoes_diretas) }}
              </span>
              <span class="badge badge-light-info">
                <feather-icon
                  icon="ClockIcon"
                  size="12"
                />
                {{ formatarMoeda(estatisticas.valor_conversoes_janela_tempo) }}
              </span>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Loading estatísticas -->
    <div
      v-else-if="carregandoStats"
      class="text-center my-3"
    >
      <b-spinner
        variant="primary"
        small
      />
      <span class="ml-2 text-muted">Carregando estatísticas...</span>
    </div>

    <!-- Lista de Campanhas -->
    <div
      v-if="!carregando && campanhasFiltradas.length > 0"
      class="campanhas-grid mt-2"
    >
      <b-card
        v-for="campanha in campanhasPaginadas"
        :key="campanha.id"
        class="campanha-card"
      >
        <!-- Mídia (Imagem ou Vídeo) -->
        <div
          v-if="campanha.image_url || campanha.video_url"
          class="campanha-imagem mb-3"
        >
          <!-- Thumbnail de Vídeo com indicador de play -->
          <div
            v-if="campanha.video_url"
            class="video-thumbnail-container"
          >
            <img
              :src="campanha.image_url || campanha.video_thumbnail"
              :alt="campanha.name"
            >
            <div class="video-play-overlay">
              <feather-icon
                icon="PlayCircleIcon"
                size="48"
                class="text-white"
              />
            </div>
            <b-badge
              variant="dark"
              class="video-badge"
            >
              <feather-icon
                icon="VideoIcon"
                size="12"
              />
              Vídeo
            </b-badge>
          </div>
          <!-- Imagem normal -->
          <img
            v-else
            :src="campanha.image_url"
            :alt="campanha.name"
          >
        </div>
        <div
          v-else
          class="campanha-imagem-placeholder mb-3"
        >
          <feather-icon
            icon="ImageIcon"
            size="48"
            class="text-muted"
          />
        </div>

        <!-- Info -->
        <h5 class="mb-1">
          {{ campanha.name }}
        </h5>
        <p class="text-muted small mb-2">
          {{ campanha.description || 'Sem descrição' }}
        </p>

        <div class="campanha-detalhes mb-3">
          <div class="mb-1">
            <small class="text-muted">Template:</small>
            <b-badge
              variant="info"
              class="ml-1"
            >
              {{ campanha.template_name }}
            </b-badge>
          </div>
          <div class="mb-1">
            <small class="text-muted">Audiência:</small>
            <span class="ml-1 font-weight-bold">{{ getNomeAudiencia(campanha.audience_id) }}</span>
          </div>
          <div class="mb-1">
            <small class="text-muted">Data de Envio:</small>
            <span class="ml-1">{{ formatarData(campanha.send_date) }}</span>
          </div>
          <div>
            <small class="text-muted">Janela de Atribuição:</small>
            <span class="ml-1">{{ campanha.attribution_window_hours }}h</span>
          </div>
        </div>

        <!-- Status da Campanha baseado em campaign_runs -->
        <div class="mb-3">
          <!-- Exibir status baseado no last_run_status -->
          <div v-if="campanha.last_run_status">
            <!-- Status: completed -->
            <div v-if="campanha.last_run_status === 'completed'">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <small class="text-muted">Status:</small>
                <b-badge
                  v-b-tooltip.hover
                  variant="success"
                  class="d-flex align-items-center p-1"
                  :title="campanha.last_sent_at ? `Enviada em ${formatarDataHora(campanha.last_sent_at)}` : 'Campanha completada'"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="14"
                    class="mr-50"
                  />
                  Enviada
                </b-badge>
              </div>
              <small
                v-if="campanha.last_sent_at"
                class="text-muted d-block text-right"
              >
                {{ formatarDataHora(campanha.last_sent_at) }}
              </small>
            </div>

            <!-- Status: scheduled (badge + switch alinhados) -->
            <div v-else-if="campanha.last_run_status === 'scheduled'">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <small class="text-muted">Status:</small>
                <div class="d-flex align-items-center">
                  <b-badge
                    v-b-tooltip.hover
                    variant="info"
                    class="d-flex align-items-center p-1 mr-2"
                    :title="`Agendada para ${formatarDataHora(campanha.send_date)}`"
                  >
                    <feather-icon
                      icon="CalendarIcon"
                      size="14"
                      class="mr-50"
                    />
                    Agendada
                  </b-badge>

                  <!-- Switch para desativar campanha agendada -->
                  <div class="custom-switch custom-control">
                    <input
                      :id="`switch-campanha-${campanha.id}`"
                      v-model="campanha.is_active"
                      type="checkbox"
                      class="custom-control-input"
                      @change="toggleStatusCampanha(campanha)"
                    >
                    <label
                      :for="`switch-campanha-${campanha.id}`"
                      class="custom-control-label"
                    >
                      <span :class="campanha.is_active ? 'text-success' : 'text-secondary'">
                        {{ campanha.is_active ? 'Ativa' : 'Inativa' }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <small class="text-muted d-block text-right">
                {{ formatarDataHora(campanha.send_date) }}
              </small>
            </div>

            <!-- Status: building -->
            <div v-else-if="campanha.last_run_status === 'building'">
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">Status:</small>
                <b-badge
                  v-b-tooltip.hover
                  variant="warning"
                  class="d-flex align-items-center p-1 badge-pulse"
                  title="Construindo lista de audiência"
                >
                  <b-spinner
                    small
                    class="mr-50"
                  />
                  Segmentando
                </b-badge>
              </div>
            </div>

            <!-- Status: ready -->
            <div v-else-if="campanha.last_run_status === 'ready'">
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">Status:</small>
                <b-badge
                  v-b-tooltip.hover
                  variant="primary"
                  class="d-flex align-items-center p-1"
                  title="Pronta para disparo"
                >
                  <feather-icon
                    icon="CheckIcon"
                    size="14"
                    class="mr-50"
                  />
                  Pronta
                </b-badge>
              </div>
            </div>

            <!-- Status: dispatching -->
            <div v-else-if="campanha.last_run_status === 'dispatching'">
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">Status:</small>
                <b-badge
                  v-b-tooltip.hover
                  variant="warning"
                  class="d-flex align-items-center p-1 badge-pulse"
                  title="Enviando mensagens"
                >
                  <b-spinner
                    small
                    class="mr-50"
                  />
                  Enviando
                </b-badge>
              </div>
            </div>

            <!-- Status: cancelled (com switch para reativar) -->
            <div v-else-if="campanha.last_run_status === 'cancelled'">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <small class="text-muted">Status:</small>
                <div class="d-flex align-items-center">
                  <b-badge
                    v-b-tooltip.hover
                    variant="warning"
                    class="d-flex align-items-center p-1 mr-2"
                    title="Execução foi cancelada manualmente"
                  >
                    <feather-icon
                      icon="XCircleIcon"
                      size="14"
                      class="mr-50"
                    />
                    Cancelada
                  </b-badge>

                  <!-- Switch para reativar campanha -->
                  <div class="custom-switch custom-control">
                    <input
                      :id="`switch-campanha-${campanha.id}`"
                      v-model="campanha.is_active"
                      type="checkbox"
                      class="custom-control-input"
                      @change="toggleStatusCampanha(campanha)"
                    >
                    <label
                      :for="`switch-campanha-${campanha.id}`"
                      class="custom-control-label"
                    >
                      <span :class="campanha.is_active ? 'text-success' : 'text-secondary'">
                        {{ campanha.is_active ? 'Ativa' : 'Inativa' }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Status: inactive (desativada manualmente, pode reativar se tempo não expirou) -->
            <div v-else-if="campanha.last_run_status === 'inactive' || (campanha.last_run_status === 'failed' && !campanha.is_active && !tempoEnvioExpirado(campanha))">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <small class="text-muted">Status:</small>
                <div class="d-flex align-items-center">
                  <b-badge
                    v-b-tooltip.hover
                    variant="secondary"
                    class="d-flex align-items-center p-1 mr-2"
                    title="Campanha desativada manualmente. Você pode reativá-la antes do tempo de envio."
                  >
                    <feather-icon
                      icon="PauseCircleIcon"
                      size="14"
                      class="mr-50"
                    />
                    Desativada
                  </b-badge>

                  <!-- Switch para reativar campanha desativada -->
                  <div class="custom-switch custom-control">
                    <input
                      :id="`switch-campanha-inactive-${campanha.id}`"
                      v-model="campanha.is_active"
                      type="checkbox"
                      class="custom-control-input"
                      @change="toggleStatusCampanha(campanha)"
                    >
                    <label
                      :for="`switch-campanha-inactive-${campanha.id}`"
                      class="custom-control-label"
                    >
                      <span :class="campanha.is_active ? 'text-success' : 'text-secondary'">
                        {{ campanha.is_active ? 'Ativa' : 'Inativa' }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <small class="text-muted d-block text-right">
                Envio previsto: {{ formatarDataHora(campanha.send_date) }}
              </small>
            </div>

            <!-- Status: failed (SEM switch - não pode reativar) -->
            <div v-else-if="campanha.last_run_status === 'failed'">
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">Status:</small>
                <b-badge
                  v-b-tooltip.hover
                  variant="danger"
                  class="d-flex align-items-center p-1"
                  title="Execução falhou devido a erro. Entre em contato com o suporte."
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="14"
                    class="mr-50"
                  />
                  Falhou
                </b-badge>
              </div>
            </div>

            <!-- Status: paused (com switch para reativar) -->
            <div v-else-if="campanha.last_run_status === 'paused'">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <small class="text-muted">Status:</small>
                <div class="d-flex align-items-center">
                  <b-badge
                    v-b-tooltip.hover
                    variant="secondary"
                    class="d-flex align-items-center p-1 mr-2"
                    title="Campanha pausada"
                  >
                    <feather-icon
                      icon="PauseCircleIcon"
                      size="14"
                      class="mr-50"
                    />
                    Pausada
                  </b-badge>

                  <!-- Switch para reativar campanha -->
                  <div class="custom-switch custom-control">
                    <input
                      :id="`switch-campanha-${campanha.id}`"
                      v-model="campanha.is_active"
                      type="checkbox"
                      class="custom-control-input"
                      @change="toggleStatusCampanha(campanha)"
                    >
                    <label
                      :for="`switch-campanha-${campanha.id}`"
                      class="custom-control-label"
                    >
                      <span :class="campanha.is_active ? 'text-success' : 'text-secondary'">
                        {{ campanha.is_active ? 'Ativa' : 'Inativa' }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Status desconhecido (fallback) -->
            <div v-else>
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">Status:</small>
                <b-badge
                  variant="secondary"
                  class="d-flex align-items-center p-1"
                >
                  {{ campanha.last_run_status }}
                </b-badge>
              </div>
            </div>
          </div>

          <!-- Se não tem execução mas o tempo expirou -->
          <div v-else-if="tempoEnvioExpirado(campanha)">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <small class="text-muted">Status:</small>
              <b-badge
                v-b-tooltip.hover
                variant="danger"
                class="d-flex align-items-center p-1"
                title="O tempo de envio programado já passou. Crie uma nova campanha para enviar novamente."
              >
                <feather-icon
                  icon="ClockIcon"
                  size="14"
                  class="mr-50"
                />
                Tempo Expirado
              </b-badge>
            </div>
            <small class="text-muted d-block text-right">
              {{ formatarDataHora(campanha.send_date) }}
            </small>
          </div>

          <!-- Senão, mostrar switch para ativar/desativar -->
          <div
            v-else
            class="d-flex align-items-center justify-content-between"
          >
            <small class="text-muted">Status:</small>
            <div class="custom-switch custom-control">
              <input
                :id="`switch-campanha-${campanha.id}`"
                v-model="campanha.is_active"
                type="checkbox"
                class="custom-control-input"
                :disabled="!campanha.can_toggle && campanha.can_toggle !== undefined"
                @change="toggleStatusCampanha(campanha)"
              >
              <label
                :for="`switch-campanha-${campanha.id}`"
                class="custom-control-label"
              >
                <span :class="campanha.is_active ? 'text-success' : 'text-secondary'">
                  {{ campanha.is_active ? 'Ativa' : 'Inativa' }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Ações -->
        <div class="d-flex gap-2 w-100 justify-content-between">
          <!-- Botão Editar - Só aparece se campanha pode ser editada -->
          <b-button
            v-if="podeEditarCampanha(campanha)"
            v-b-tooltip.hover
            variant="outline-success"
            size="sm"
            title="Editar campanha (até 10 min antes do envio)"
            @click="editarCampanha(campanha)"
          >
            <feather-icon
              icon="EditIcon"
              size="14"
            />
          </b-button>

          <b-button
            variant="outline-primary"
            size="sm"
            class="w-100"
            :block="!podeEditarCampanha(campanha)"
            @click="verMetricas(campanha)"
          >
            <feather-icon
              icon="BarChart2Icon"
              size="14"
              class="mr-50"
            />
            Métricas
          </b-button>
          <b-button
            variant="outline-danger"
            size="sm"
            @click="confirmarExclusao(campanha)"
          >
            <feather-icon
              icon="TrashIcon"
              size="14"
            />
          </b-button>
        </div>
      </b-card>
    </div>

    <!-- Paginação -->
    <div
      v-if="!carregando && totalPaginas > 1"
      class="d-flex justify-content-center align-items-center mt-3 mb-2"
    >
      <b-pagination
        v-model="paginaAtual"
        :total-rows="campanhasFiltradas.length"
        :per-page="campanhasPorPagina"
        first-number
        last-number
        prev-text="‹"
        next-text="›"
        align="center"
        class="mb-0"
      />
      <small class="text-muted ml-3">
        {{ (paginaAtual - 1) * campanhasPorPagina + 1 }}–{{ Math.min(paginaAtual * campanhasPorPagina, campanhasFiltradas.length) }}
        de {{ campanhasFiltradas.length }} campanhas
      </small>
    </div>

    <!-- Estado vazio -->
    <b-card
      v-if="!carregando && campanhasFiltradas.length === 0"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="64"
        class="text-muted mb-3"
      />
      <h4 class="text-muted">
        Nenhuma campanha encontrada
      </h4>
      <p class="text-muted mb-3">
        Crie sua primeira campanha oficial
      </p>
      <b-button
        variant="primary"
        @click="abrirModalNovaCampanha"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        />
        Criar Campanha
      </b-button>
    </b-card>

    <!-- Modal Nova Campanha -->
    <modal-nova-campanha
      v-model="modalNovaCampanhaVisivel"
      :campanha-edicao="campanhaEmEdicao"
      :pode-salvar-edicao="!campanhaEmEdicao || podeEditarCampanha(campanhaEmEdicao)"
      :store-uuid="$route.params.uuid"
      @salvar="salvarCampanha"
    />

    <!-- Modal Envio de Teste -->
    <modal-envio-teste
      v-model="modalEnvioTesteVisivel"
      :store-uuid="$route.params.uuid"
    />

    <!-- Modal Campanhas Deletadas -->
    <b-modal
      v-model="modalDeletadasVisivel"
      title="Campanhas Deletadas"
      size="xl"
      ok-only
      ok-title="Fechar"
      scrollable
    >
      <div
        v-if="carregandoDeletadas"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          Carregando campanhas deletadas...
        </p>
      </div>

      <div
        v-else-if="!campanhasDeletadas.length"
        class="text-center py-5"
      >
        <feather-icon
          icon="Trash2Icon"
          size="48"
          class="text-muted mb-2"
        />
        <p class="text-muted">
          Nenhuma campanha deletada encontrada
        </p>
      </div>

      <b-table
        v-else
        :items="campanhasDeletadas"
        :fields="camposTabelaDeletadas"
        responsive
        striped
        hover
        show-empty
        sticky-header="500px"
      >
        <template #cell(name)="data">
          <div>
            <strong>{{ data.item.name }}</strong>
            <br>
            <small class="text-muted">
              <feather-icon
                icon="LayersIcon"
                size="12"
              />
              {{ data.item.template_name }}
            </small>
          </div>
        </template>

        <template #cell(send_date)="data">
          <small>{{ formatarData(data.item.send_date) }}</small>
        </template>

        <template #cell(deleted_at)="data">
          <b-badge variant="light-danger">
            <feather-icon
              icon="TrashIcon"
              size="12"
            />
            {{ formatarData(data.item.deleted_at) }}
          </b-badge>
        </template>

        <template #cell(stats)="data">
          <div class="text-center">
            <div>
              <small class="text-muted">Execuções:</small>
              <strong>{{ data.item.total_runs || 0 }}</strong>
            </div>
            <div v-if="data.item.last_sent_at">
              <small class="text-muted">Última execução:</small>
              <br>
              <small>{{ formatarData(data.item.last_sent_at) }}</small>
            </div>
          </div>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormInput, BFormSelect, BFormCheckbox, BSpinner, BBadge, BAlert, BModal, BTable,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import {
  listarCampanhas, criarCampanha, atualizarCampanha, excluirCampanha as excluirCampanhaAPI, buscarAudiencias, buscarEstatisticasCampanhas, toggleCampanhaAtiva, cancelarExecucaoCampanha, buscarStatusTelefone, buscarMetricasCampanha, listarCampanhasDeletadas,
} from '@/functions/basics/campaign-oficial'
import ModalNovaCampanha from './ModalNovaCampanha.vue'
import ModalEnvioTeste from './ModalEnvioTeste.vue'

export default {
  name: 'CampanhasOficiais',
  components: {
    BCard,
    BButton,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BSpinner,
    BBadge,
    BAlert,
    BModal,
    BTable,
    FeatherIcon,
    ModalNovaCampanha,
    ModalEnvioTeste,
  },
  data() {
    return {
      campanhas: [],
      audiencias: [],
      estatisticas: null,
      statusTelefone: null,
      carregando: false,
      carregandoStats: false,
      mensagemLoading: 'Carregando...', // Mensagem dinâmica do loading
      busca: '',
      filtroStatus: 'todas',
      modalNovaCampanhaVisivel: false,
      modalDeletadasVisivel: false,
      modalEnvioTesteVisivel: false,
      campanhaEmEdicao: null,
      campanhasDeletadas: [],
      carregandoDeletadas: false,
      camposTabelaDeletadas: [
        { key: 'name', label: 'Campanha', sortable: true },
        { key: 'send_date', label: 'Data de Envio', sortable: true },
        { key: 'deleted_at', label: 'Deletada em', sortable: true },
        { key: 'stats', label: 'Estatísticas', thClass: 'text-center' },
      ],
      statusOptions: [
        { value: 'todas', text: 'Todas as Campanhas' },
        { value: 'agendadas', text: 'Agendadas' },
        { value: 'enviadas', text: 'Enviadas' },
        { value: 'finalizadas', text: 'Finalizadas' },
      ],
      paginaAtual: 1,
      campanhasPorPagina: 10,
    }
  },
  computed: {
    campanhasFiltradas() {
      let resultado = [...this.campanhas]

      // Filtro de busca
      if (this.busca) {
        const termo = this.busca.toLowerCase()
        resultado = resultado.filter(c => c.name.toLowerCase().includes(termo)
          || c.description?.toLowerCase().includes(termo)
          || c.template_name.toLowerCase().includes(termo))
      }

      // Filtro de status (implementar lógica baseada em send_date)
      if (this.filtroStatus !== 'todas') {
        const agora = new Date()
        resultado = resultado.filter(c => {
          const dataEnvio = new Date(c.send_date)
          if (this.filtroStatus === 'agendadas') {
            return dataEnvio > agora
          } if (this.filtroStatus === 'enviadas') {
            return dataEnvio <= agora
          }
          return true
        })
      }

      return resultado
    },
    totalPaginas() {
      return Math.ceil(this.campanhasFiltradas.length / this.campanhasPorPagina)
    },
    campanhasPaginadas() {
      const inicio = (this.paginaAtual - 1) * this.campanhasPorPagina
      return this.campanhasFiltradas.slice(inicio, inicio + this.campanhasPorPagina)
    },
    variantStatus() {
      if (!this.statusTelefone || !this.statusTelefone.data) return 'info'
      const rating = this.statusTelefone.data.quality_rating
      if (rating === 'GREEN') return 'success'
      if (rating === 'YELLOW') return 'warning'
      if (rating === 'RED') return 'danger'
      return 'info'
    },
    tituloStatus() {
      if (!this.statusTelefone || !this.statusTelefone.data) return 'Verificando...'
      const rating = this.statusTelefone.data.quality_rating
      if (rating === 'GREEN') return 'Saudável'
      if (rating === 'YELLOW') return 'Atenção'
      if (rating === 'RED') return 'Perigo'
      return 'Status Desconhecido'
    },
    iconStatus() {
      if (!this.statusTelefone || !this.statusTelefone.data) return 'InfoIcon'
      const rating = this.statusTelefone.data.quality_rating
      if (rating === 'GREEN') return 'CheckCircleIcon'
      if (rating === 'YELLOW') return 'AlertTriangleIcon'
      if (rating === 'RED') return 'AlertOctagonIcon'
      return 'InfoIcon'
    },
    qualityRating() {
      if (!this.statusTelefone || !this.statusTelefone.data) return null
      return this.statusTelefone.data.quality_rating
    },
  },
  watch: {
    busca() {
      this.paginaAtual = 1
    },
    filtroStatus() {
      this.paginaAtual = 1
    },
  },
  mounted() {
    this.carregarCampanhas()
    this.carregarAudiencias()
    this.carregarEstatisticas()
    this.carregarStatusTelefone()
  },
  methods: {
    async carregarCampanhas() {
      try {
        const storeUuid = this.$route.params.uuid
        const campanhas = await listarCampanhas(storeUuid)

        // Converter is_active de número (0/1) para booleano
        this.campanhas = (campanhas || []).map(campanha => ({
          ...campanha,
          is_active: Boolean(campanha.is_active),
        }))
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error)
        this.$bvToast.toast('Erro ao carregar campanhas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async carregarStatusTelefone() {
      try {
        const storeUuid = this.$route.params.uuid
        const status = await buscarStatusTelefone(storeUuid)
        this.statusTelefone = status
      } catch (error) {
        console.error('Erro ao carregar status do telefone:', error)
        // Não mostrar toast de erro para não poluir a interface
        // O alerta simplesmente não aparecerá se houver erro
      }
    },

    abrirModalNovaCampanha() {
      this.campanhaEmEdicao = null
      this.modalNovaCampanhaVisivel = true
    },

    abrirModalEnvioTeste() {
      this.modalEnvioTesteVisivel = true
    },

    editarCampanha(campanha) {
      // Verificar novamente se pode editar antes de abrir o modal
      if (!this.podeEditarCampanha(campanha)) {
        this.$bvToast.toast('Esta campanha não pode mais ser editada', {
          title: 'Não é possível editar',
          variant: 'warning',
          solid: true,
        })
        return
      }

      // Preparar os dados da campanha para edição
      this.campanhaEmEdicao = {
        ...campanha,
        // Garantir que is_active seja booleano
        is_active: Boolean(campanha.is_active),
      }

      this.modalNovaCampanhaVisivel = true
    },

    /** Mesma regra do backend: send_date em BRT, edição só com >= 10 min até o envio. */
    envioComPeloMenos10MinutosAntecedencia(campanha) {
      if (!campanha?.send_date) return true
      try {
        const s = String(campanha.send_date).trim().replace(' ', 'T')
        const comTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}-03:00`
        const ms = new Date(comTz).getTime() - Date.now()
        if (Number.isNaN(ms)) return true
        return ms >= 10 * 60 * 1000
      } catch (e) {
        return true
      }
    },

    podeEditarCampanha(campanha) {
      // Não pode editar se já foi enviada (completed)
      if (campanha.last_run_status === 'completed') {
        return false
      }

      // Não pode editar se já foi enviada (verificação adicional)
      if (this.campanhaFoiEnviada(campanha)) {
        return false
      }

      // Não pode editar se o tempo de envio já passou
      if (this.tempoEnvioExpirado(campanha)) {
        return false
      }

      // Não pode editar se está em processo de envio (dispatching)
      if (campanha.last_run_status === 'dispatching') {
        return false
      }

      // Não pode editar se está construindo audiência (building)
      if (campanha.last_run_status === 'building') {
        return false
      }

      if (campanha.can_edit === false) {
        return false
      }

      if (campanha.can_edit !== true && !this.envioComPeloMenos10MinutosAntecedencia(campanha)) {
        return false
      }

      // Pode editar se está agendada, inativa, cancelada, pausada, pronta ou falhou
      return ['scheduled', 'inactive', 'cancelled', 'paused', 'ready', 'failed', null].includes(campanha.last_run_status)
    },

    verMetricas(campanha) {
      this.$router.push({
        name: 'campaign-oficiais-metricas',
        params: {
          uuid: this.$route.params.uuid,
          campanhaId: String(campanha.id),
        },
      })
    },

    async abrirModalCampanhasDeletadas() {
      this.modalDeletadasVisivel = true
      this.carregandoDeletadas = true

      try {
        const storeUuid = this.$route.params.uuid
        // Buscar apenas campanhas deletadas
        this.campanhasDeletadas = await listarCampanhasDeletadas(storeUuid)
      } catch (error) {
        console.error('Erro ao buscar campanhas deletadas:', error)
        this.$bvToast.toast('Erro ao carregar campanhas deletadas', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregandoDeletadas = false
      }
    },

    async salvarCampanha(campanha) {
      // Verificar se é edição ou criação
      const isEdicao = !!(campanha.id)

      // Mostrar loading com mensagem personalizada
      this.mensagemLoading = isEdicao ? 'Atualizando campanha...' : 'Criando campanha...'
      this.carregando = true

      try {
        const storeUuid = this.$route.params.uuid

        if (isEdicao) {
          // Atualizar campanha existente
          console.log('📝 Editando campanha:', campanha.id)
          await atualizarCampanha(storeUuid, campanha.id, campanha)

          // Fechar modal imediatamente
          this.modalNovaCampanhaVisivel = false

          // Atualizar mensagem de loading
          this.mensagemLoading = 'Campanha atualizada! Atualizando lista...'

          // Mostrar mensagem de sucesso
          this.$bvToast.toast('Campanha atualizada com sucesso!', {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          })
        } else {
          // Criar nova campanha
          console.log('🆕 Criando nova campanha')
          await criarCampanha(campanha, storeUuid)

          // Fechar modal imediatamente
          this.modalNovaCampanhaVisivel = false

          // Atualizar mensagem de loading
          this.mensagemLoading = 'Campanha criada! Atualizando lista...'

          // Mostrar mensagem de sucesso
          this.$bvToast.toast('Campanha criada com sucesso!', {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
            autoHideDelay: 3000,
          })
        }

        // Recarregar dados sem refresh da página
        await Promise.all([
          this.carregarEstatisticas(),
          this.carregarCampanhas(),
        ])

        // Remover loading após atualização
        this.carregando = false
      } catch (error) {
        console.error('Erro ao salvar campanha:', error)

        // Remover loading em caso de erro
        this.carregando = false

        // Extrair mensagem de erro mais específica
        let mensagemErro = `Erro ao ${isEdicao ? 'atualizar' : 'salvar'} campanha. Verifique os dados e tente novamente.`

        if (error.response?.data?.error) {
          mensagemErro = error.response.data.error

          // Se houver detalhes da validação, mostrar também
          if (error.response.data.details && Array.isArray(error.response.data.details)) {
            const detalhes = error.response.data.details
              .map(d => `${d.field}: ${d.message}`)
              .join('\n')
            mensagemErro += `\n\nDetalhes:\n${detalhes}`
          }
        }

        const errCode = error.response?.data?.code
        if (errCode === 'CAMPAIGN_EDIT_WINDOW_CLOSED' || errCode === 'SEND_DATE_TOO_SOON') {
          this.$bvToast.toast(mensagemErro, {
            title: 'Prazo para edição',
            variant: 'warning',
            solid: true,
            autoHideDelay: 8000,
          })
          return
        }

        this.$bvToast.toast(mensagemErro, {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 8000,
        })
      }
    },

    async toggleStatusCampanha(campanha) {
      // Capturar o novo estado desejado e reverter imediatamente para manter UI estável durante confirmação
      const novoEstado = campanha.is_active
      campanha.is_active = !novoEstado // Reverter para estado anterior

      // Se está tentando ativar a campanha, solicitar confirmação
      if (novoEstado) {
        // Verificar se o tempo de envio expirou
        if (this.tempoEnvioExpirado(campanha)) {
          this.$bvToast.toast('O tempo de envio desta campanha já passou. Crie uma nova campanha para enviar novamente.', {
            title: 'Tempo Expirado',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
          })
          return
        }

        // Confirmação básica de ativação
        const confirmarAtivacao = await this.$bvModal.msgBoxConfirm(
          `Tem certeza que deseja ativar a campanha "${campanha.name}"? A campanha será enviada na data agendada.`,
          {
            title: '✅ Confirmar Ativação',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'success',
            okTitle: 'Sim, ativar',
            cancelTitle: 'Cancelar',
            cancelVariant: 'outline-secondary',
            centered: true,
          },
        )

        if (!confirmarAtivacao) {
          return
        }

        // Verificar restrições de saúde
        const rating = this.qualityRating

        // RED (Perigo) - Bloquear ativação
        if (rating === 'RED') {
          this.$bvModal.msgBoxOk(
            'Não é possível ativar a campanha pois a saúde do seu número WhatsApp está muito baixa (Perigo). Por favor, melhore a qualidade do número antes de ativar campanhas.',
            {
              title: '🚨 Saúde Crítica - Ativação Bloqueada',
              size: 'md',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'Entendi',
              headerClass: 'bg-danger text-white',
              centered: true,
            },
          )
          return
        }

        // YELLOW (Atenção) - Avisar mas permitir
        if (rating === 'YELLOW') {
          const confirmarComAviso = await this.$bvModal.msgBoxConfirm(
            'A saúde do seu número WhatsApp está em nível de Atenção. Recomendamos cautela ao enviar campanhas para evitar bloqueios. Deseja continuar mesmo assim?',
            {
              title: '⚠️ Atenção - Saúde Baixa',
              size: 'md',
              buttonSize: 'sm',
              okVariant: 'warning',
              okTitle: 'Sim, ativar',
              cancelTitle: 'Cancelar',
              cancelVariant: 'outline-secondary',
              headerClass: 'bg-warning text-dark',
              centered: true,
            },
          )

          if (!confirmarComAviso) {
            return
          }
        }
      }

      // Aplicar o novo estado após confirmação
      const statusAnterior = campanha.last_run_status
      campanha.is_active = novoEstado

      // Atualizar status local para evitar flash de "Falhou" durante a transição
      if (novoEstado && (statusAnterior === 'failed' || statusAnterior === 'inactive')) {
        campanha.last_run_status = 'scheduled'
      } else if (!novoEstado && statusAnterior === 'scheduled') {
        campanha.last_run_status = 'inactive'
      }

      // Prosseguir com a ativação/desativação
      try {
        const storeUuid = this.$route.params.uuid
        await toggleCampanhaAtiva(storeUuid, campanha.id, campanha.is_active)

        this.$bvToast.toast(
          `Campanha ${campanha.is_active ? 'ativada' : 'desativada'} com sucesso!`,
          {
            title: 'Sucesso',
            variant: 'success',
            solid: true,
          },
        )

        // Recarregar campanhas para atualizar os status
        await Promise.all([
          this.carregarEstatisticas(),
          this.carregarCampanhas(),
        ])
      } catch (error) {
        console.error('Erro ao alterar status da campanha:', error)

        // Reverter o switch e status em caso de erro
        campanha.is_active = !campanha.is_active
        campanha.last_run_status = statusAnterior

        // Verificar tipo de erro
        const errorMsg = error.response?.data?.error || error.message
        const errorCode = error.response?.data?.code

        // Erro: Campanha já enviada (completed)
        if (errorCode === 'CAMPAIGN_ALREADY_SENT') {
          const details = error.response?.data?.details || {}

          this.$bvModal.msgBoxOk(
            details.message || 'Esta campanha já foi enviada e não pode ser alterada. Crie uma nova campanha para enviar novamente.',
            {
              title: '⚠️ Campanha Já Enviada!',
              size: 'md',
              buttonSize: 'sm',
              okVariant: 'warning',
              okTitle: 'Entendi',
              headerClass: 'bg-warning text-dark',
              centered: true,
            },
          )

          // Atualizar os campos da campanha com informações do backend
          if (details.completed_at) {
            campanha.last_sent_at = details.completed_at
            campanha.already_sent = true
            campanha.can_toggle = false
          }

          // Recarregar lista para garantir dados atualizados
          this.carregarCampanhas()
          return
        }

        if (errorCode === 'CAMPAIGN_DISPATCH_IN_PROGRESS') {
          const details = error.response?.data?.details || {}
          this.$bvModal.msgBoxOk(
            details.message || errorMsg || 'O disparo já iniciou (montagem de audiência ou envio). Não é possível cancelar.',
            {
              title: 'Disparo em andamento',
              size: 'md',
              buttonSize: 'sm',
              okVariant: 'warning',
              okTitle: 'Entendi',
              headerClass: 'bg-warning text-dark',
              centered: true,
            },
          )
          this.carregarCampanhas()
          return
        }

        // Erro: Execução ativa
        if (errorMsg && errorMsg.includes('execução ativa')) {
          // Extrair o Run ID da mensagem de erro (ex: "Run ID: 58")
          const runIdMatch = errorMsg.match(/Run ID: (\d+)/)
          const runId = runIdMatch ? parseInt(runIdMatch[1]) : null

          // Mostrar modal para cancelar a execução
          this.$bvModal.msgBoxConfirm(
            `Esta campanha possui uma execução agendada ativa (Run ID: ${runId || 'desconhecido'}). Para desativar a campanha, você precisa cancelar o agendamento primeiro. Deseja cancelar agora?`,
            {
              title: '⚠️ Execução Ativa Detectada',
              size: 'md',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'Sim, cancelar agendamento',
              cancelTitle: 'Não, manter agendamento',
              cancelVariant: 'outline-secondary',
              headerClass: 'bg-warning text-dark',
              centered: true,
            },
          ).then(async confirmarCancelamento => {
            if (confirmarCancelamento && runId) {
              await this.cancelarExecucao(campanha, runId)
            }
          })
        } else {
          // Erro genérico
          this.$bvToast.toast(errorMsg || 'Erro ao alterar status da campanha', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        }
      }
    },

    confirmarExclusao(campanha) {
      this.$bvModal.msgBoxConfirm(
        `Tem certeza que deseja excluir a campanha "${campanha.name}"?`,
        {
          title: 'Confirmar Exclusão',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Sim, excluir',
          cancelTitle: 'Cancelar',
          centered: true,
        },
      ).then(confirmado => {
        if (confirmado) {
          this.excluirCampanha(campanha.id)
        }
      })
    },

    async excluirCampanha(id) {
      try {
        const storeUuid = this.$route.params.uuid
        await excluirCampanhaAPI(storeUuid, id)

        // Mostrar toast de sucesso
        this.$bvToast.toast('Campanha excluída com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        // Recarregar dados sem refresh da página
        await Promise.all([
          this.carregarEstatisticas(),
          this.carregarCampanhas(),
        ])

        // Remover loading após atualização
        this.carregando = false
      } catch (error) {
        console.error('Erro ao excluir campanha:', error)

        // Remover loading em caso de erro
        this.carregando = false

        const code = error.response?.data?.code
        const msg = error.response?.data?.error || 'Erro ao excluir campanha'
        if (code === 'CAMPAIGN_DISPATCH_IN_PROGRESS') {
          this.$bvToast.toast(msg, {
            title: 'Disparo em andamento',
            variant: 'warning',
            solid: true,
            autoHideDelay: 6000,
          })
        } else {
          this.$bvToast.toast(msg, {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        }
      }
    },

    async cancelarExecucao(campanha, runId) {
      try {
        const storeUuid = this.$route.params.uuid

        this.$bvToast.toast('Cancelando agendamento...', {
          title: 'Aguarde',
          variant: 'info',
          solid: true,
        })

        await cancelarExecucaoCampanha(storeUuid, campanha.id, runId)

        // Após cancelar, tentar desativar a campanha
        campanha.is_active = false
        await toggleCampanhaAtiva(storeUuid, campanha.id, false)

        this.$bvToast.toast('Agendamento cancelado e campanha desativada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
        })

        // Recarregar lista de campanhas
        await this.carregarCampanhas()
        this.carregarEstatisticas()
      } catch (error) {
        console.error('Erro ao cancelar execução:', error)

        const errorMsg = error.response?.data?.error || error.message

        this.$bvToast.toast(errorMsg || 'Erro ao cancelar agendamento', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
        })
      }
    },

    async carregarAudiencias() {
      try {
        const storeUuid = this.$route.params.uuid
        const audiencias = await buscarAudiencias(storeUuid)
        this.audiencias = audiencias || []
      } catch (error) {
        console.error('Erro ao carregar audiências:', error)
        this.audiencias = []
      }
    },

    async carregarEstatisticas() {
      this.carregandoStats = true
      try {
        const storeUuid = this.$route.params.uuid
        const response = await buscarEstatisticasCampanhas(storeUuid)

        // A API retorna { success, data: { campanhas: {...}, conversoes: {...}, mensagens: {...}, ... } }
        const data = response.data || response

        console.log('📊 Estatísticas recebidas:', data)

        // Função auxiliar para limpar valores monetários com problema (ex: "0.0061708.26" -> "61708.26")
        const limparValorMonetario = valor => {
          if (!valor) return 0
          const valorStr = String(valor)
          // Se tiver múltiplos pontos, pegar apenas a última parte numérica válida
          const match = valorStr.match(/(\d+\.\d+)$/)
          return match ? parseFloat(match[1]) : parseFloat(valorStr) || 0
        }

        // Calcular lucro total (soma dos lucros individuais de cada campanha)
        let lucroTotal = 0
        const lucroDirecto = 0
        const lucroJanelaTempo = 0

        if (this.campanhas && this.campanhas.length > 0) {
          // Buscar métricas de cada campanha para somar os lucros
          const promessasMetricas = this.campanhas.map(async campanha => {
            try {
              const metricas = await buscarMetricasCampanha(storeUuid, campanha.id)
              return metricas.data?.roi || null
            } catch (error) {
              console.error(`Erro ao buscar métricas da campanha ${campanha.id}:`, error)
              return null
            }
          })

          const resultados = await Promise.all(promessasMetricas)

          resultados.forEach(roi => {
            if (roi && roi.lucro) {
              lucroTotal += parseFloat(roi.lucro) || 0
            }
          })

          console.log('💰 Lucro total calculado:', lucroTotal)
        }

        // Mapear os dados da API para o formato esperado pelo componente
        this.estatisticas = {
          // Campanhas
          total_campanhas: data.campanhas?.total || 0,
          campanhas_ativas: data.campanhas?.ativas || 0,
          campanhas_inativas: data.campanhas?.inativas || 0,
          campanhas_deletadas: data.campanhas?.deletadas || 0,

          // Conversões
          total_conversoes: data.conversoes?.total || 0,
          conversoes_diretas: data.conversoes?.conversoes_diretas?.total || 0,
          conversoes_janela_tempo: data.conversoes?.conversoes_janela_tempo?.total || 0,

          // Lucro calculado pela soma dos lucros individuais
          valor_total_conversoes: lucroTotal,
          valor_conversoes_diretas: lucroDirecto,
          valor_conversoes_janela_tempo: lucroJanelaTempo,
        }

        console.log('📊 Estatísticas mapeadas:', this.estatisticas)
      } catch (error) {
        console.error('Erro ao carregar estatísticas:', error)
        this.estatisticas = null
      } finally {
        this.carregandoStats = false
      }
    },

    getNomeAudiencia(audienceId) {
      if (!audienceId) return 'N/A'

      const audiencia = this.audiencias.find(a => a.value === audienceId)
      return audiencia ? audiencia.text : `Audiência #${audienceId}`
    },

    formatarData(data) {
      if (!data) return 'N/A'
      return new Date(data).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo', // Força timezone de Brasília
      })
    },

    campanhaFoiEnviada(campanha) {
      // Verificar se a campanha já foi enviada (status completed no backend)
      // Prioriza o campo 'already_sent' retornado pelo backend
      if (campanha.already_sent !== undefined) {
        return campanha.already_sent
      }

      // Fallback: verificar se tem execuções completadas
      if (campanha.completed_runs !== undefined && campanha.completed_runs > 0) {
        return true
      }

      // Fallback: Verifica se a campanha tem execuções (foi enviada)
      if (campanha.execucoes_total && parseInt(campanha.execucoes_total) > 0) {
        return true
      }

      return false
    },

    tempoEnvioExpirado(campanha) {
      // Se já foi enviada, não é expirada
      if (this.campanhaFoiEnviada(campanha)) {
        return false
      }

      // Se não tem data de envio, não pode estar expirada
      if (!campanha.send_date) {
        return false
      }

      try {
        const dataEnvio = new Date(campanha.send_date)
        const agora = new Date()

        // Considera expirada se a data de envio já passou
        return dataEnvio < agora
      } catch (error) {
        console.error('Erro ao verificar tempo de envio:', error)
        return false
      }
    },

    formatarPorcentagem(valor) {
      if (valor === null || valor === undefined) return '0%'
      return `${parseFloat(valor).toFixed(1)}%`
    },

    formatarNumero(valor) {
      if (valor === null || valor === undefined) return '0'
      return new Intl.NumberFormat('pt-BR').format(valor)
    },

    formatarMoeda(valor) {
      if (valor === null || valor === undefined) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(valor)
    },

    formatarDataHora(dataISO) {
      if (!dataISO) return ''
      const data = new Date(dataISO)
      return data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.campanhas-oficiais {
  padding: 1rem;
  position: relative;
}

/* Loading Fullscreen Centralizado */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  animation: fadeIn 0.3s ease-in-out;
}

.loading-content {
  text-align: center;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.418);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  min-width: 300px;
}

.loading-content h4 {
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}

.loading-content p {
  font-size: 0.95rem;
  color: #6c757d;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.cursor-pointer {
  cursor: pointer;
  transition: all 0.2s ease;
}

.cursor-pointer:hover {
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Animação de pulsação para badges em progresso */
@keyframes badge-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.02);
  }
}

.badge-pulse {
  animation: badge-pulse 2s ease-in-out infinite;
}

/* Grid Principal - Cards Destacados */
.stats-grid-primary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

.stat-card {
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.stat-card .card-body {
  display: flex;
  align-items: center;
  padding: 1.25rem;
}

/* Card Destacado */
.stat-card-highlight {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid #7367f0 !important;
}

.stat-card-highlight .card-body {
  padding: 1.5rem;
}

.stat-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #f8f9fa;
  margin-right: 1rem;
  flex-shrink: 0;
}

.stat-icon-large {
  width: 64px;
  height: 64px;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
}

.stat-value-large {
  font-size: 2.25rem;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.25rem;
}

.stat-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.stat-breakdown .badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.campanhas-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
}

.campanha-card {
  transition: all 0.3s ease;
  height: 100%;
}

.campanha-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.campanha-imagem {
  width: 100%;
  height: 200px;
  overflow: hidden;
  border-radius: 8px;
  background: #f8f9fa;
}

.campanha-imagem img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.campanha-imagem-placeholder {
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

/* Estilos para thumbnail de vídeo */
.video-thumbnail-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-thumbnail-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.9;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.video-thumbnail-container:hover .video-play-overlay {
  opacity: 1;
}

.video-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.campanha-detalhes {
  font-size: 0.9rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 1400px) {
  .campanhas-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 1200px) {
  .stats-grid-primary {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  .campanhas-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .stats-grid-primary {
    grid-template-columns: 1fr;
  }

  .campanhas-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-value {
    font-size: 1.5rem;
  }

  .stat-value-large {
    font-size: 1.75rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .stat-icon-large {
    width: 56px;
    height: 56px;
  }

  .stat-card-highlight .card-body {
    padding: 1.25rem;
  }
}

@media (max-width: 480px) {
  .stats-grid-primary {
    grid-template-columns: 1fr;
  }

  .stat-value {
    font-size: 1.25rem;
  }

  .stat-value-large {
    font-size: 1.5rem;
  }

  .stat-breakdown {
    flex-direction: column;
    gap: 0.35rem;
  }

  .stat-breakdown .badge {
    width: 100%;
    justify-content: center;
  }
}
</style>
